/**
 * ATB (Add To Bag) delivery/EDD block builder for the "Add To Bag Clicked"
 * segment event.
 *
 * Combines the two shapes the EDD API can land in:
 *
 *   1. Aggregator (single-seller) — `state.edd.eddDetails[pincode][size]` with
 *      standard fields spread at the top level and `priorityDelivery` /
 *      `rushDelivery` as nested sub-objects (see edd/reducers/index.js
 *      DELIVERY_DETAILS.SUCCESS). This is what single-seller products expose.
 *
 *   2. Multi-seller / B2C — `state.edd.slaDetails[pincode][size].options[]`.
 *      The aggregator slice stays empty; each option carries its seller's
 *      delivery info under `serviceabilityDetails.productDetails[]`, each
 *      entry tagged with `deliveryMethod` (STANDARD / PRIORITY / RUSH).
 *
 * Dates must be ISO strings (e.g. "2026-10-07T19:12:52+0530"). The SLA code
 * field (`deliverySLA`, values like "4HR" / "NDD") is NEVER a date and never
 * flows into `*_edd` fields — using it there was the pre-fix bug that fed
 * "4HR" into `standard_delivery_edd` on multi-seller rush products.
 *
 * The `delivery_type` string is built from whichever branch supplied the data
 * (aggregator's `.servicability` / nested SLAs, or the SLA option's methods).
 *
 * @param {object} args
 * @param {object} args.eddDetails Aggregator slice via getEddDetails selector.
 * @param {object[]} args.slaOptions Multi-seller SLA option array.
 * @param {object} args.selectedSlaOption The user-picked SLA option (if any).
 * @returns {object} pdRushData payload for gtmAddToCart.
 */
export function buildAtbPdRushData({
  eddDetails = {},
  slaOptions = [],
  selectedSlaOption = null
} = {}) {
  // ------- aggregator path (single-seller) -------
  const pdData = eddDetails?.priorityDelivery;
  const rushData = eddDetails?.rushDelivery;

  // ------- multi-seller / B2C fallback -------
  const options = Array.isArray(slaOptions) ? slaOptions : [];
  const pickedSla =
    (options.find(o => o?.priceGroupId === selectedSlaOption?.priceGroupId) ||
      options.find(o => o?.isSelected) ||
      options[0]) ||
    null;
  const slaProductDetails = Array.isArray(
    pickedSla?.serviceabilityDetails?.productDetails
  )
    ? pickedSla.serviceabilityDetails.productDetails
    : [];
  const slaServicability =
    pickedSla?.serviceabilityDetails?.servicability ??
    pickedSla?.serviceabilityDetails?.serviceability ??
    slaProductDetails[0]?.servicability ??
    slaProductDetails[0]?.serviceability;

  const getIsoDate = pd =>
    pd?.eddUpper ||
    pd?.promiseShippingDate ||
    pd?.dateOfDelivery ||
    pd?.eddLower ||
    '';

  const findSlaByMethod = method =>
    slaProductDetails.find(
      pd => String(pd?.deliveryMethod || '').toUpperCase().trim() === method
    ) || null;

  // STANDARD also catches entries with no explicit deliveryMethod, mirroring
  // the aggregator reducer which spreads a method-less product into the
  // top-level standard slot.
  const standardEntry =
    slaProductDetails.find(pd => {
      const m = String(pd?.deliveryMethod || '').toUpperCase().trim();
      return m === 'STANDARD' || m === '';
    }) || null;
  const slaStandardEdd = getIsoDate(standardEntry);
  const slaPriorityEdd = getIsoDate(findSlaByMethod('PRIORITY'));
  const slaRushEdd = getIsoDate(findSlaByMethod('RUSH'));

  // ------- delivery_type: comma-separated list of available methods -------
  const deliveryTypes = [];
  if (eddDetails?.servicability || slaServicability || standardEntry) {
    deliveryTypes.push('Standard');
  }
  if (pdData?.deliverySLA || findSlaByMethod('PRIORITY')) {
    deliveryTypes.push('Priority|NDD');
  }
  if (rushData?.deliverySLA || findSlaByMethod('RUSH')) {
    deliveryTypes.push('Rush|1HR');
  }

  return {
    // Aggregator keys first (pre-existing single-seller behavior preserved),
    // then multi-seller SLA fallback. deliverySLA deliberately omitted — see
    // module-level JSDoc.
    standard_delivery_edd:
      eddDetails?.promiseShippingDate ||
      eddDetails?.eddUpper ||
      eddDetails?.eddLower ||
      slaStandardEdd ||
      'NA',
    priority_delivery_edd: pdData?.deliverySLA || slaPriorityEdd || 'NA',
    rush_delivery_edd: rushData?.deliverySLA || slaRushEdd || 'NA',
    delivery_type: deliveryTypes.join(','),
    product_servicability:
      eddDetails?.servicability ?? slaServicability ?? false,
    pincode: eddDetails?.activePincode
  };
}
